// Blog Service
// Fetches blogs from API and transforms to match component expectations

import { useQuery } from "@tanstack/react-query";
import { generateSlug } from "../utils/slugUtils";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "https://insightpanel.sembian.net";

/**
 * Format date string to readable format
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date string
 */
const formatDate = (dateString) => {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch (e) {
    return dateString;
  }
};

/**
 * Transform API blog data to match component expectations
 */
const transformBlog = (apiBlog) => {
  // Construct full image URL
  let imageUrl = null;
  if (apiBlog.image) {
    // If image path already starts with http/https, use as is
    if (
      apiBlog.image.startsWith("http://") ||
      apiBlog.image.startsWith("https://")
    ) {
      imageUrl = apiBlog.image;
    } else {
      // Otherwise, prepend API base URL
      imageUrl = apiBlog.image.startsWith("/")
        ? `${API_BASE_URL}${apiBlog.image}`
        : `${API_BASE_URL}/${apiBlog.image}`;
    }
  }

  // Format dates
  const publishedDate = formatDate(apiBlog.created_at || apiBlog.updated_at);

  return {
    id: apiBlog.id,
    title: apiBlog.title || "",
    image: imageUrl,
    bannerImg: imageUrl, // Use same image for banner
    category: apiBlog.meta_name || "Blog",
    author: "Admin", // Default author since API doesn't provide
    publishedDate: publishedDate,
    descripTion: apiBlog.description || "",
    description: apiBlog.description || "", // Also provide as description
    meta_name: apiBlog.meta_name || "",
    meta_description: apiBlog.meta_description || "",
    created_at: apiBlog.created_at,
    updated_at: apiBlog.updated_at,
    // Keep original data for reference if needed
    _original: apiBlog,
  };
};

/**
 * Get all blogs
 * @returns {Promise<Array>} Array of all blogs
 */
export const getAllBlogs = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/blogs`);
    const result = await response.json();

    if (result.success && result.data) {
      // Transform each blog
      return result.data.map(transformBlog);
    }

    console.error("API response error:", result);
    return [];
  } catch (error) {
    console.error("Error fetching blogs:", error);
    return [];
  }
};

/**
 * Get single blog by ID
 * @param {number|string} id - Blog ID
 * @returns {Promise<Object|null>} Blog object or null if not found
 */
export const getBlogById = async (id) => {
  try {
    const blogs = await getAllBlogs();
    const blog = blogs.find((b) => b.id === parseInt(id));
    return blog || null;
  } catch (error) {
    console.error("Error loading blog:", error);
    return null;
  }
};

/**
 * Get single blog by slug
 * @param {string} slug - Blog slug (from title)
 * @returns {Promise<Object|null>} Blog object or null if not found
 */
export const getBlogBySlug = async (slug) => {
  try {
    const blogs = await getAllBlogs();
    const blog = blogs.find((b) => generateSlug(b.title) === slug);
    return blog || null;
  } catch (error) {
    console.error("Error loading blog:", error);
    return null;
  }
};

// Export utility functions for direct use if needed
export { generateSlug };

/**
 * React Query hook to get all blogs
 * @returns {Object} Query result with data, isLoading, error, etc.
 */
export const useBlogs = () => {
  return useQuery({
    queryKey: ["blogs"],
    queryFn: getAllBlogs,
    staleTime: 30 * 60 * 1000, // 30 minutes - blogs don't change frequently
    gcTime: 60 * 60 * 1000, // 1 hour - keep in cache for 1 hour
  });
};

/**
 * React Query hook to get a single blog by ID
 * @param {number|string} id - Blog ID
 * @returns {Object} Query result with data, isLoading, error, etc.
 */
export const useBlogById = (id) => {
  return useQuery({
    queryKey: ["blog", id],
    queryFn: () => getBlogById(id),
    enabled: !!id,
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
  });
};

/**
 * React Query hook to get a single blog by slug
 * @param {string} slug - Blog slug
 * @returns {Object} Query result with data, isLoading, error, etc.
 */
export const useBlogBySlug = (slug) => {
  return useQuery({
    queryKey: ["blog", "slug", slug],
    queryFn: () => getBlogBySlug(slug),
    enabled: !!slug,
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
  });
};
